import React from 'react';
import { css } from '@emotion/react';

interface CommentTextProps {
  text: string;
  mentions?: Array<{
    id: string;
    fname: string;
    lname: string;
  }>;
}

/**
 * Component to display comment text with mentions.
 * Mentions are in the format <@userid> and are displayed with special styling.
 * Newlines are converted to <br /> elements for proper display.
 */
export const CommentText: React.FC<CommentTextProps> = ({
  text,
  mentions = [],
}) => {
  // Parse text and extract mentions and newlines
  const mentionRegex = /<@([^>]+)>/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  // Process the text to handle mentions
  const processTextSegment = (textSegment: string): React.ReactNode[] => {
    const segmentParts: React.ReactNode[] = [];
    let segmentLastIndex = 0;
    let segmentMatch;

    mentionRegex.lastIndex = 0; // Reset regex state

    while ((segmentMatch = mentionRegex.exec(textSegment)) !== null) {
      // Add text before mention
      if (segmentMatch.index > segmentLastIndex) {
        segmentParts.push(
          <span key={key++}>
            {textSegment.slice(segmentLastIndex, segmentMatch.index)}
          </span>,
        );
      }

      // Add mention
      const userId = segmentMatch[1];
      // Find the user in the mentions array
      const mentionedUser = mentions.find((m) => m.id === userId);
      const displayName = mentionedUser
        ? `${mentionedUser.fname} ${mentionedUser.lname}`
        : 'Unknown User';

      segmentParts.push(
        <span
          key={key++}
          css={(theme) => css`
            color: ${theme.deepOcean};
            font-weight: 500;
          `}
        >
          @{displayName}
        </span>,
      );

      segmentLastIndex = segmentMatch.index + segmentMatch[0].length;
    }

    // Add remaining text in segment
    if (segmentLastIndex < textSegment.length) {
      segmentParts.push(
        <span key={key++}>{textSegment.slice(segmentLastIndex)}</span>,
      );
    }

    return segmentParts.length > 0
      ? segmentParts
      : [<span key={key++}>{textSegment}</span>];
  };

  // Split by newlines and process each line
  const lines = text.split('\n');
  lines.forEach((line, index) => {
    if (index > 0) {
      // Add line break between lines
      parts.push(<br key={key++} />);
    }
    parts.push(...processTextSegment(line));
  });

  return <>{parts}</>;
};
